const revealSelector = [
  '.jesoli-featured-catalogue__header',
  '.jesoli-featured-catalogue__collection-header',
  '.jesoli-about__copy',
  '.jesoli-faq__heading',
  '.jesoli-contact__copy',
].join(',');

const controllers = new WeakMap();
const activeRoots = new Set();
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

class JesoliRevealController {
  #observer;
  #root;
  #targets = [];

  constructor(root) {
    this.#root = root;
  }

  initialize() {
    if (reducedMotionQuery.matches || !('IntersectionObserver' in window)) return;

    this.#targets = this.#findTargets();
    if (!this.#targets.length) return;

    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      rootMargin: '0px 0px 8% 0px',
      threshold: 0.08,
    });

    this.#targets.forEach((target) => {
      target.classList.add('jesoli-reveal-pending');
      this.#observer.observe(target);
    });
  }

  destroy() {
    this.#observer?.disconnect();
    this.#observer = undefined;

    for (const target of this.#targets) {
      target.classList.remove('jesoli-reveal-pending', 'is-visible');
    }

    this.#targets = [];
  }

  #findTargets() {
    const targets = [...this.#root.querySelectorAll(revealSelector)];
    if (this.#root instanceof Element && this.#root.matches(revealSelector)) targets.unshift(this.#root);
    return [...new Set(targets)].filter((target) => target instanceof HTMLElement);
  }

  #handleIntersection = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      entry.target.classList.add('is-visible');
      this.#observer?.unobserve(entry.target);
    }
  };
}

const rootContainsRevealContent = (root) =>
  root instanceof Element && (root.matches(revealSelector) || root.querySelector(revealSelector));

const initializeRoot = (root) => {
  if (!rootContainsRevealContent(root) || controllers.has(root)) return;

  const controller = new JesoliRevealController(root);
  controllers.set(root, controller);
  activeRoots.add(root);
  controller.initialize();
};

const destroyRoot = (root) => {
  const controller = controllers.get(root);
  if (!controller) return;

  controller.destroy();
  controllers.delete(root);
  activeRoots.delete(root);
};

const initializePageSections = () => {
  for (const root of document.querySelectorAll('.shopify-section')) initializeRoot(root);
};

const handleMotionPreference = () => {
  for (const root of [...activeRoots]) destroyRoot(root);
  initializePageSections();
};

initializePageSections();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initializeRoot(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof Element) destroyRoot(event.target);
});

reducedMotionQuery.addEventListener('change', handleMotionPreference);
